<template>
  <div class="swiper-container relative">
    <swiper-container
      ref="swiperRef"
      :init="false"
      :class="overflowSwiper && 'overflow-swiper'"
      :css-mode="(thumbnail || overflowSwiper) ? false : true"
      :slides-per-view="'auto'"
      :free-mode="overflowSwiper && true"
      :breakpoints="(!thumbnail && !overflowSwiper) && swiperBreakpoints"
      :autoplay="thumbnail ? { delay: 3000, disableOnInteraction: false } : false"
      space-between="30"
      :loop="thumbnail && !overflowSwiper && props.images?.length > 3"
      :rewind="!thumbnail && props.images?.length > 3"
      :effect="thumbnail ? 'fade' : 'slide'"
      :navigation-next-el="`#next-button-${uuid}`"
      :navigation-prev-el="`#prev-button-${uuid}`"
      :pagination="true"
      :pagination-el="`#dot-nav-${uuid}`"
      :pagination-clickable="!thumbnail"
    >
      <swiper-slide
        v-for="image in props.images"
      >
        <MediaElement
          :src="image.path" 
          :alt="image.description"
          @loaded="imageLoaded"
        ></MediaElement>
      </swiper-slide>
    </swiper-container>

    <div 
      class="control-container" 
      :class="thumbnail && 'thumbnail-control-container'"
    >
      <div
        class="dot-nav"
        :class="thumbnail && 'thumbnail-dot-nav'"
        :id="`dot-nav-${uuid}`"
      ></div>

      <div class="button-nav">
        <div 
          v-if="!thumbnail"
          class="swiper-control swiper-prev-button"
          :id="`prev-button-${uuid}`"
        >f</div>
        <div
          v-if="!thumbnail"
          class="swiper-control swiper-next-button"
          :id="`next-button-${uuid}`"
        >f</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { register } from 'swiper/element/bundle'
import { useRequestStore } from '@/stores/requests'
import { v4 as uuidv4 } from 'uuid'

register()

const props = defineProps<{
    images: any
    pID?: number
    source?: any
    thumbnail?: boolean
}>()

const uuid = uuidv4().slice(0, 8)

const requestStore = useRequestStore()
const { pageContent, pageID } = storeToRefs(requestStore)

const emit = defineEmits(['loaded'])

const swiperRef = ref(null)

const swiperBreakpoints = computed(() => {
    const widgets = pageContent.value[pageID.value]?.pagecontent.widgets
    const widgetContent = widgets?.find((widget: any) => widget.meta.cID === props.pID)

    let breakpoints = {
        1024: {
            slidesPerView: widgetContent?.meta.swiper_columns
        },
        640: {
            slidesPerView: widgetContent?.meta.swiper_columns > 1 ? 2 : 1
        }
    }

    return breakpoints
})

const overflowSwiper = computed(() => {
    const widgets = pageContent.value[pageID.value]?.pagecontent.widgets
    const widgetContent = widgets?.find((widget: any) => widget.meta.cID === props.pID)

    return widgetContent?.meta.swiper_overflow
})

const isSwiperAutoplayStarted = ref(false)
let initializationTimeout: any = null

const initializeSwiper = () => {
  if (swiperRef.value) {
    const swiperComponent = swiperRef.value as any;

    if (swiperComponent.swiper) {
        swiperComponent.swiper.destroy(true, true); 
        // console.warn('Existing Swiper instance destroyed before re-initialization.');
    }

    if (overflowSwiper.value === true) {
      const overflowParams = {
        injectStyles: [
          `
          .swiper,
          .swiper-slide {
            overflow: visible;
          }
          `
        ],
      }
      Object.assign(swiperRef.value, overflowParams)
    }

    try {
      nextTick(() => { 
        swiperComponent.initialize();

        if (initializationTimeout) {
          clearTimeout(initializationTimeout)
        }

        if (!isSwiperAutoplayStarted.value && props.thumbnail) {
          (swiperRef.value as any)?.swiper.autoplay.start()
          isSwiperAutoplayStarted.value = true
        }
      }); 

    } catch (e) {
      // console.error('Swiper initialization failed, retrying...')
      initializationTimeout = setTimeout(initializeSwiper, 50)
    }
  }
}

const loadedImagesCount = ref(0); 
const swiperReady = ref(false);

const imageLoaded = () => {
  emit('loaded')

  loadedImagesCount.value++;
  
  if (loadedImagesCount.value >= props.images.length) {
    if (!swiperReady.value) {
      nextTick(() => {
        initializeSwiper();
        swiperReady.value = true;
      });
    }
  }
};

onUnmounted(() => {
  if (initializationTimeout) {
    clearTimeout(initializationTimeout)
  }
})
</script>